import PropTypes from "prop-types"
import { capitalize } from "lodash"
import { DESC } from "helpers/sorts"
import { useAssetViewDashboardMetrics } from "helpers/metrics/hooks"
import { getPerformanceFilterInstrumentColumns } from "helpers/metrics"
import { ONE_DAY } from "helpers/dates"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import { useNormalPerformancePricingRefreshWarning } from "gatherers/hooks/use-normal-performance-pricing-refresh-warning"
import ContributorsTable from "renderers/contributors-datagrid"
import { CLASSIFICATIONS_SHAPE } from "helpers/shapes/asset-view"
import { useAssetViewTemplateChart } from "../../hooks/use-asset-view-template-chart"
import { ASSET_VIEW_PERFORMANCE_CHART } from "../template-chart-config"
import { useAnalytics } from "connected-components/analytics"
import { useAssetViewData } from "connected-components/asset-view/hooks/use-asset-view-data"
import { useAssetViewHeaderHandlers } from "../../hooks/use-asset-view-header-handlers"

export default function AssetViewPerformance({ modelMetadata, instrumentsWithMetadata, highlightedRowIds, disableActiveTrading, instrumentColumns, classifications }) {
  const { selectedMetricDisplay, selectedCategory } = useAssetViewTemplateChart(ASSET_VIEW_PERFORMANCE_CHART)
  const contextData = useAssetViewData()
  const {
    instrument: original,
    performanceEndDate,
    base: contextBase,
    groupBy,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setTradedPosition,
    selectedTableLevels,
    setSelectedTableLevels,
    isExpanded,
    performanceStartDate,
    period,
    isEndVsStartDateComparison,
    startDate,
    endDate,
  } = contextData

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useAssetViewHeaderHandlers({ track: analytics.track })

  const apiStartDate = period === ONE_DAY ? performanceEndDate : performanceStartDate

  const gatherers = useTemplateGatherers(contextData, "performance", {
    selectedMetricDisplay,
    selectedCategory,
    classifications,
    from: apiStartDate,
    to: performanceEndDate,
  })

  useNormalPerformancePricingRefreshWarning({ gatherers, startDate, endDate })

  const metrics = useAssetViewDashboardMetrics(
    [{
      category: "performance",
      selectedMetricDisplay,
      selectedCategory,
      filterInstrumentColumns: getPerformanceFilterInstrumentColumns(isEndVsStartDateComparison),
    }],
    original,
    rebalanced,
    comparison,
    { instrumentColumns, isActive: !!contextBase, modelMetadata, instrumentsWithMetadata, setTradedPosition, isExpanded, disableActiveTrading, isEndVsStartDateComparison },
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]].suffix

  return <ContributorsDataIntegrator
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    rendererProps={{
      highlightedRowIds,
      groupByName: groupBy?.label,
      isPositionAttributeGroup: !!groupBy?.isPositionAttribute,
      defaultSort: {
        path: `marketValue${capitalize(defaultSortSuffix)}`,
        direction: DESC,
      },
      downloadKey: "assets.performance",
      downloadName: "Assets - Performance",
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      // Analytics callbacks
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
    }}
  />
}

AssetViewPerformance.propTypes = {
  modelMetadata: PropTypes.object.isRequired,
  instrumentsWithMetadata: PropTypes.object.isRequired,
  instrumentsWithQueryOptions: PropTypes.object.isRequired,
  highlightedRowIds: PropTypes.arrayOf(PropTypes.string),
  disableActiveTrading: PropTypes.bool.isRequired,
  instrumentColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  classifications: CLASSIFICATIONS_SHAPE.isRequired,
}
